import { useState, useCallback, useEffect } from 'react';
import type { ConnectionConfig } from '../types/telegram';
import type { SSEStatus } from '../hooks/useSSE';
import { usePersistedState } from '../hooks/usePersistedState';

interface ConnectionSetupProps {
  onConnect: (config: ConnectionConfig) => void;
  onDisconnect: () => void;
  status: SSEStatus;
}

const statusLabels: Record<SSEStatus, { text: string; color: string; icon: string }> = {
  disconnected: { text: 'Disconnected', color: 'text-gray-500', icon: '\u2B58' },
  connecting: { text: 'Connecting...', color: 'text-yellow-500', icon: '\u25D0' },
  connected: { text: 'Connected', color: 'text-green-500', icon: '\u25CF' },
  error: { text: 'Connection Error', color: 'text-red-500', icon: '\u2715' },
};

export function ConnectionSetup({
  onConnect,
  onDisconnect,
  status,
}: ConnectionSetupProps): React.JSX.Element {
  const [savedWebhookUrl, setSavedWebhookUrl] = usePersistedState('webhookUrl', '');
  const [savedSseUrl, setSavedSseUrl] = usePersistedState('sseUrl', '');

  const [webhookUrl, setWebhookUrl] = useState(savedWebhookUrl);
  const [sseUrl, setSseUrl] = useState(savedSseUrl);
  const [sessionId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    setWebhookUrl(savedWebhookUrl);
  }, [savedWebhookUrl]);

  useEffect(() => {
    setSseUrl(savedSseUrl);
  }, [savedSseUrl]);

  const handleConnect = useCallback(() => {
    if (!webhookUrl.trim() || !sseUrl.trim()) {
      return;
    }

    setSavedWebhookUrl(webhookUrl.trim());
    setSavedSseUrl(sseUrl.trim());

    onConnect({
      webhookUrl: webhookUrl.trim(),
      sseUrl: sseUrl.trim(),
      sessionId,
    });
  }, [webhookUrl, sseUrl, sessionId, onConnect, setSavedWebhookUrl, setSavedSseUrl]);

  const handleDisconnect = useCallback(() => {
    onDisconnect();
  }, [onDisconnect]);

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';
  const statusInfo = statusLabels[status];

  return (
    <div className="rounded-lg bg-telegram-secondary-bg p-4 dark:bg-telegram-secondary-bg-dark">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Bot Connection</h2>
        <div className={`flex items-center gap-2 text-sm ${statusInfo.color}`}>
          <span>{statusInfo.icon}</span>
          <span>{statusInfo.text}</span>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label
            htmlFor="webhookUrl"
            className="mb-1 block text-sm text-telegram-text-secondary dark:text-telegram-text-secondary-dark"
          >
            Webhook URL
          </label>
          <input
            id="webhookUrl"
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://your-bot.com/webhook"
            disabled={isConnected || isConnecting}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-telegram-blue focus:outline-none focus:ring-1 focus:ring-telegram-blue disabled:opacity-50 dark:border-gray-600 dark:bg-telegram-bg-dark"
          />
        </div>

        <div>
          <label
            htmlFor="sseUrl"
            className="mb-1 block text-sm text-telegram-text-secondary dark:text-telegram-text-secondary-dark"
          >
            SSE Debug URL
          </label>
          <input
            id="sseUrl"
            type="url"
            value={sseUrl}
            onChange={(e) => setSseUrl(e.target.value)}
            placeholder="https://your-bot.com/debug"
            disabled={isConnected || isConnecting}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-telegram-blue focus:outline-none focus:ring-1 focus:ring-telegram-blue disabled:opacity-50 dark:border-gray-600 dark:bg-telegram-bg-dark"
          />
          <p className="mt-1 text-xs text-telegram-text-secondary dark:text-telegram-text-secondary-dark">
            Session ID will be appended: {sessionId.slice(0, 8)}...
          </p>
        </div>

        <div className="flex gap-2">
          {isConnected ? (
            <button
              onClick={handleDisconnect}
              className="flex-1 rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600"
            >
              Disconnect
            </button>
          ) : (
            <button
              onClick={handleConnect}
              disabled={!webhookUrl.trim() || !sseUrl.trim() || isConnecting}
              className="flex-1 rounded-md bg-telegram-blue px-4 py-2 text-sm font-medium text-white hover:bg-telegram-blue-dark disabled:opacity-50"
            >
              {isConnecting ? 'Connecting...' : 'Connect'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
